// =============================================================================
// Phase 7: Output Formatter
// Normalizes date/time input to consistent format for display
// =============================================================================

import { MONTHS_SHORT, MONTHS_FULL, LOWERCASE_KEYWORDS, ALL_WEEKDAYS } from "./utils/constants";

// Set version of weekdays for quick lookup
const WEEKDAYS = new Set(ALL_WEEKDAYS);

/**
 * Format output string with consistent casing and spacing
 */
export function formatOutput(input: string): string {
  // Handle empty/whitespace input
  const trimmed = input.trim();
  if (!trimmed) return "";

  // Normalize multiple spaces to single space
  let normalized = trimmed.replace(/\s+/g, " ");

  // Check if it's an expression (contains now or starts with operator)
  if (isExpression(normalized)) {
    return formatExpression(normalized);
  }

  // Check if it's an ISO date (don't modify)
  if (isISODate(normalized)) {
    return normalized;
  }

  // Process tokens
  const tokens = splitIntoTokens(normalized);
  const formatted = formatTokens(tokens);

  return formatted;
}

/**
 * Check if input is a Grafana-style expression
 */
function isExpression(input: string): boolean {
  const lower = input.toLowerCase();
  // Starts with now, or operator, or contains expression operators
  if (lower.startsWith("now") || /^[-+/]/.test(lower)) {
    return true;
  }
  // Contains expression pattern anywhere (for ranges)
  if (/\bnow\b/i.test(input) && /[-+/]\d*[smhdwMy]/i.test(input)) {
    return true;
  }
  // Simple expression like -5m, /d
  if (/^[-+/]\d*[smhdwMy]$/i.test(input)) {
    return true;
  }
  return false;
}

/**
 * Check if input is an ISO date
 */
function isISODate(input: string): boolean {
  // YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS patterns
  return /^\d{4}-\d{2}-\d{2}/.test(input);
}

/**
 * Format expression - lowercase keywords but preserve M (months)
 * M must stay uppercase to distinguish from m (minutes)
 */
function formatExpression(input: string): string {
  // Handle ranges with expressions
  if (/\s+to\s+/i.test(input)) {
    const parts = input.split(/\s+(to)\s+/i);
    return parts.map((part, i) => {
      if (i === 1) return "to"; // separator
      return formatExpression(part.trim());
    }).join(" ");
  }

  // Lowercase all characters except M (which distinguishes months from minutes)
  return input.split("").map(c => c === "M" ? "M" : c.toLowerCase()).join("");
}

/**
 * Split input into tokens (simple space-based splitting for formatting)
 * Note: This is different from tokenizer.ts which does full lexical analysis for parsing
 */
function splitIntoTokens(input: string): string[] {
  // Split on spaces but keep track of special patterns
  const tokens: string[] = [];
  const parts = input.split(" ");

  for (const part of parts) {
    if (!part) continue;
    tokens.push(part);
  }

  return tokens;
}

/**
 * Format tokens with appropriate casing
 */
function formatTokens(tokens: string[]): string {
  const result: string[] = [];
  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];
    const nextToken = tokens[i + 1];

    // Check if current token is a number/time and next is AM/PM
    if (nextToken && /^(\d{1,2})(:\d{2})?(:\d{2})?$/.test(token) && /^[aApP]\.?[mM]\.?$/.test(nextToken)) {
      // Combine time with am/pm
      const ampm = nextToken.replace(/\./g, "").toLowerCase();
      result.push(token + ampm);
      i += 2;
      continue;
    }

    // Check if token is standalone AM/PM with period format (like "P.M." or "A.M.")
    if (/^[aApP]\.?[mM]\.?$/.test(token) && result.length > 0) {
      // Check if previous result is a time
      const prev = result[result.length - 1];
      if (/^\d{1,2}(:\d{2})?(:\d{2})?$/.test(prev)) {
        // Combine with previous
        const ampm = token.replace(/\./g, "").toLowerCase();
        result[result.length - 1] = prev + ampm;
        i++;
        continue;
      }
    }

    const formatted = formatToken(token, tokens, i);
    result.push(formatted);
    i++;
  }

  return result.join(" ");
}

/**
 * Format a single token
 */
function formatToken(token: string, tokens: string[], index: number): string {
  const lower = token.toLowerCase();

  // Check for quarter pattern (Q1, Q2, etc.)
  if (/^q[1-4]$/i.test(token)) {
    return token.toUpperCase();
  }

  // Check for month name
  const monthFormatted = formatMonth(token);
  if (monthFormatted) {
    return monthFormatted;
  }

  // Check for AM/PM time
  const timeFormatted = formatTime(token);
  if (timeFormatted) {
    return timeFormatted;
  }

  // Check for European time format (14H, 14H30)
  if (/^\d{1,2}h\d*$/i.test(token)) {
    return token.toLowerCase();
  }

  // Check for lowercase keyword
  if (LOWERCASE_KEYWORDS.has(lower)) {
    return lower;
  }

  // Check for range separators
  if (lower === "to" || token === "-" || token === "~" || token === "->") {
    return lower === "to" ? "to" : token;
  }

  // Default: keep as-is (numbers, punctuation, etc.)
  return token;
}

/**
 * Format month name to Title Case
 */
function formatMonth(token: string): string | null {
  const lower = token.toLowerCase();

  // Check short month names
  if (MONTHS_SHORT.includes(lower)) {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  // Check full month names
  if (MONTHS_FULL.includes(lower)) {
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  return null;
}

/**
 * Format time with AM/PM normalization
 */
function formatTime(token: string): string | null {
  // Pattern: H(:MM)?(:SS)? ?[aApP]\.?[mM]\.?
  // Examples: 2PM, 2pm, 2 PM, 2:30PM, 2:30pm, 2P.M., 2:30p.m.

  // Handle time with a.m./p.m. format
  const periodMatch = token.match(/^(\d{1,2}(?::\d{2}(?::\d{2})?)?)\s*([aApP])\.?([mM])\.?$/);
  if (periodMatch) {
    const time = periodMatch[1];
    const ampm = (periodMatch[2] + periodMatch[3]).toLowerCase();
    return time + ampm;
  }

  // Handle time with am/pm (no period)
  const ampmMatch = token.match(/^(\d{1,2}(?::\d{2}(?::\d{2})?)?)\s*([aApP][mM])$/);
  if (ampmMatch) {
    const time = ampmMatch[1];
    const ampm = ampmMatch[2].toLowerCase();
    return time + ampm;
  }

  return null;
}
